<template>
  <div v-click-outside="close">
    <button
      ref="triggerRef"
      type="button"
      class="inline-flex items-center justify-center rounded-full"
      :class="[{ 'text-primary-2': isOpen }]"
      :data-testid="testId"
      @click="toggle"
    >
      <em :class="['mdi', iconClass, 'text-2xl']"></em>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="fixed min-w-90 z-50 rounded-md border border-gray-200 bg-white p-2 text-left text-sm whitespace-pre-line break-words shadow-lg dark:border-dark-3 dark:bg-dark-2"
        :style="tooltipStyle"
        @click.stop
      >
        <slot>{{ message }}</slot>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    message?: string;
    position?: 'top' | 'right' | 'bottom' | 'left';
    iconClass?: string;
    testId?: string;
  }>(),
  {
    message: '',
    position: 'top',
    iconClass: 'mdi-help-circle-outline',
    testId: 'help-icon',
  }
);

const isOpen = ref(false);

const triggerRef = ref<HTMLElement | null>(null);
const coords = ref({ top: 0, left: 0, transform: '' });
const gap = 8;

const tooltipStyle = computed(() => ({
  top: `${coords.value.top}px`,
  left: `${coords.value.left}px`,
  transform: coords.value.transform,
}));

const updatePosition = () => {
  const el = triggerRef.value;
  if (!el) return;
  const r = el.getBoundingClientRect();

  switch (props.position) {
    case 'right':
      coords.value = {
        top: r.top + r.height / 2 + window.scrollY,
        left: r.right + window.scrollX + gap,
        transform: 'translate(0, -50%)',
      };
      break;
    case 'bottom':
      coords.value = {
        top: r.bottom + window.scrollY + gap,
        left: r.left + r.width / 2 + window.scrollX,
        transform: 'translate(-50%, 0)',
      };
      break;
    case 'left':
      coords.value = {
        top: r.top + r.height / 2 + window.scrollY,
        left: r.left + window.scrollX - gap,
        transform: 'translate(-100%, -50%)',
      };
      break;
    default:
      coords.value = {
        top: r.top + window.scrollY - gap,
        left: r.left + r.width / 2 + window.scrollX,
        transform: 'translate(-50%, -100%)',
      };
  }
};

const onWindowChange = () => {
  if (isOpen.value) updatePosition();
};

onMounted(() => {
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
});

watch(isOpen, (value) => {
  if (value) updatePosition();
});

const toggle = () => {
  isOpen.value = !isOpen.value;
};
const close = () => {
  isOpen.value = false;
};
</script>
